import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTwilioCall } from "./TwilioCallProvider";
import { MIC_PERMISSION } from "./useTwilioAudioDevices";

const SYSTEM_DEFAULT = "";

/**
 * Microphone, headphones and network settings for the browser phone.
 *
 * Collapsed by default: this is the drawer people open to make a call, not to
 * configure hardware, so it stays out of the way until something is wrong or
 * the user goes looking for it.
 */
const TwilioAudioSettings = () => {
  const {
    micPermission = MIC_PERMISSION.UNKNOWN,
    micPermissionError = "",
    audioInputDevices = [],
    audioOutputDevices = [],
    audioPrefs = {},
    isOutputSelectionSupported = false,
    isVolumeSupported = false,
    inputVolume = 0,
    requestMicPermission = () => {},
    selectInputDevice = () => {},
    selectOutputDevice = () => {},
    selectRingtoneDevice = () => {},
    testSpeakerDevice = () => {},
    startAudioPreview = () => {},
    stopAudioPreview = () => {},
    isForcingTurnRelay = false,
    setForceTurnRelay = () => {},
    hasConnectedCall = false,
  } = useTwilioCall();

  const [isOpen, setIsOpen] = useState(false);

  const isGranted = micPermission === MIC_PERMISSION.GRANTED;
  const isDenied = micPermission === MIC_PERMISSION.DENIED;
  const needsPrompt = micPermission === MIC_PERMISSION.PROMPT;

  /*
   * Opening the panel acquires the microphone so the level meter has something
   * to read. Closing it hands the microphone back, unless a call is using it,
   * so the browser's recording indicator is not left on for the whole session.
   */
  useEffect(() => {
    if (!isOpen) return undefined;

    startAudioPreview();

    return () => {
      stopAudioPreview({ keepStream: hasConnectedCall });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAllowMicrophone = useCallback(async () => {
    await requestMicPermission();
    await startAudioPreview();
  }, [requestMicPermission, startAudioPreview]);

  const renderDeviceSelect = ({
    label,
    value,
    options,
    onChange,
    disabled,
    helperText,
  }) => (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={value || SYSTEM_DEFAULT}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      helperText={helperText}
    >
      <MenuItem value={SYSTEM_DEFAULT}>System default</MenuItem>

      {options.map((option) => (
        <MenuItem key={option.deviceId} value={option.deviceId}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Paper variant="outlined" className="twilio-call-drawer__audio">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className="twilio-call-drawer__audio-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Audio and network
          </Typography>

          <Typography variant="body2" fontWeight={700}>
            {isGranted ? "Microphone ready" : "Microphone not approved"}
            {isForcingTurnRelay ? " · relay" : ""}
          </Typography>
        </Box>

        <IconButton size="small" aria-label="Audio settings">
          <i
            className={`fa-solid ${
              isOpen ? "fa-chevron-up" : "fa-sliders"
            }`}
          />
        </IconButton>
      </Stack>

      <Collapse in={isOpen} unmountOnExit>
        <Stack spacing={2} className="twilio-call-drawer__audio-body">
          {isDenied && (
            <Alert severity="error">
              {micPermissionError ||
                "Microphone access is blocked. Allow it in your browser's site settings for this page, then reload."}
            </Alert>
          )}

          {needsPrompt && (
            <Alert
              severity="warning"
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleAllowMicrophone}
                >
                  Allow
                </Button>
              }
            >
              Approve the microphone before your first call, so you are not
              prompted part way through dialling.
            </Alert>
          )}

          {!isGranted && !isDenied && !needsPrompt && micPermissionError && (
            <Alert severity="warning">{micPermissionError}</Alert>
          )}

          {renderDeviceSelect({
            label: "Microphone",
            value: audioPrefs.inputDeviceId,
            options: audioInputDevices,
            onChange: selectInputDevice,
            disabled: !isGranted,
            helperText: isGranted
              ? undefined
              : "Approve the microphone to name and choose a device.",
          })}

          {isVolumeSupported && isGranted && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Input level
              </Typography>

              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.round(inputVolume * 100))}
                className="twilio-call-drawer__audio-meter"
              />

              <Typography variant="caption" color="text.secondary">
                Speak, and this should move.
              </Typography>
            </Box>
          )}

          <Divider />

          {isOutputSelectionSupported ? (
            <>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box flex={1}>
                  {renderDeviceSelect({
                    label: "Call audio (headphones)",
                    value: audioPrefs.outputDeviceId,
                    options: audioOutputDevices,
                    onChange: selectOutputDevice,
                    disabled: false,
                    helperText: undefined,
                  })}
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={testSpeakerDevice}
                  className="twilio-call-drawer__audio-test"
                >
                  Test
                </Button>
              </Stack>

              {renderDeviceSelect({
                label: "Incoming ringtone",
                value: audioPrefs.ringtoneDeviceId,
                options: audioOutputDevices,
                onChange: selectRingtoneDevice,
                disabled: false,
                helperText:
                  "Leave this on the speakers so you still hear a call with your headset off.",
              })}
            </>
          ) : (
            <Alert severity="info">
              This browser cannot choose an output device, so calls use whatever
              your operating system has set. Chrome and Edge can.
            </Alert>
          )}

          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={isForcingTurnRelay}
                onChange={(event) => setForceTurnRelay(event.target.checked)}
              />
            }
            label="Route call audio through a TURN relay"
          />

          <Typography variant="caption" color="text.secondary">
            Turn this on if calls connect and ring but stay silent. It sends
            audio over TCP on port 443, which gets through networks that block
            the UDP that calls normally use. It adds a little latency, so leave
            it off unless you need it. It switches on by itself the first time a
            call fails for want of an audio path.
          </Typography>
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default TwilioAudioSettings;
